import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { setDayjsLocale } from '@/libs/dayjs'
import { useFetchStore } from '@/stores/fetch'
import type { ISettings } from '@/types/Settings'
import type { IUser } from '@/types/User'

// Mirrors the backend default until GET /settings answers.
const DEFAULT_SETTINGS: ISettings = { edit_window_days: 7, weekend_counts_quota: false }

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IUser>()
    const settings     = ref<ISettings>({ ...DEFAULT_SETTINGS })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // Sets a JWT cookie so image/PDF requests via <img src>/<a href> are authenticated.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    const setUser = (user: IUser | undefined) => {
        userData.value = user
        if (user) loadSettings()
    }

    const setSettings = (value: ISettings) => {
        settings.value = value
    }

    const loadSettings = async () => {
        const { statusCode, data } = await useFetchStore().fetch('/settings').get().json<ISettings>()
        if (statusCode.value === 200 && data.value) settings.value = data.value
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => (loaderActive.value = Math.max(0, loaderActive.value - 1))

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
        setDayjsLocale(newLocale)
    }

    return {
        JWT, locale, userData, settings, loaderActive, windowWidth,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setUser, setSettings, setLogout, startLoading, stopLoading, changeLocale,
    }
})
